import * as React from "react";
import { Download, RefreshCw, ScrollText } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { downloadAuditCsv, useAuditLogs } from "@/lib/auditExport/api";

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

export function AuditExportPage() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "audit-export",
    defaultPageSize: 25,
  });
  const [downloading, setDownloading] = React.useState(false);

  const logsQuery = useAuditLogs({ limit: pageSize, offset: page * pageSize });
  const total = logsQuery.data?.total ?? 0;
  const items = React.useMemo(() => logsQuery.data?.items ?? [], [logsQuery.data?.items]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadAuditCsv();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = "audit-export.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      toast.success("Audit export downloaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not download audit export.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl py-8">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Audit export</PageHeaderHeading>
            <PageHeaderDescription>
              Tamper-evident record of who did what — exportable for DPDPA and internal review.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void logsQuery.refetch()}
              disabled={logsQuery.isFetching}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", logsQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            <Button size="sm" onClick={() => void handleDownload()} disabled={downloading}>
              <Download aria-hidden="true" className="size-4" />
              Export CSV
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <div className="mt-6">
        <DataTable>
          <DataTableToolbar>
            <ScrollText aria-hidden="true" className="text-muted-foreground size-4" />
            <span className="text-muted-foreground text-sm">Audit log</span>
            <DataTableToolbarSpacer />
          </DataTableToolbar>
          {logsQuery.isLoading ? (
            <DataTableSkeleton columns={5} rows={8} />
          ) : logsQuery.isError ? (
            <div className="border-border/60 bg-muted/30 text-muted-foreground rounded-md border p-6 text-sm">
              Could not load audit log.{" "}
              <button type="button" className="underline" onClick={() => void logsQuery.refetch()}>
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <DataTableEmpty
              title="No audit entries yet"
              description="Actions across your centre will appear here as they happen."
            />
          ) : (
            <>
              <DataTableScroll>
                <Table>
                  <caption className="sr-only">Audit log entries</caption>
                  <TableHeader className="bg-background sticky top-0 z-10">
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Role</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-border/60 divide-y">
                    {items.map((row) => (
                      <TableRow key={row.id} className="hover:bg-muted/40">
                        <TableCell>{formatWhen(row.occurred_at)}</TableCell>
                        <TableCell>{row.action}</TableCell>
                        <TableCell>
                          {row.resource_type}
                          {row.resource_id ? ` · ${row.resource_id}` : ""}
                        </TableCell>
                        <TableCell>{row.actor_id ?? "—"}</TableCell>
                        <TableCell>{row.actor_role ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataTableScroll>
              <DataTableFooter>
                <DataTablePagination
                  page={page + 1}
                  pageSize={pageSize}
                  totalRows={total}
                  onPageChange={(next) => setPage(Math.max(0, next - 1))}
                  onPageSizeChange={(next) => {
                    setPageSize(next);
                    setPage(0);
                  }}
                />
              </DataTableFooter>
            </>
          )}
        </DataTable>
      </div>
    </div>
  );
}
